import React, { useState, useEffect, useCallback } from 'react';
import { Reply, Pencil, Trash2, Shield, Check, X, Pin, MessageSquare, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { RankChip } from '../../RankBadge';
import { Composer } from '../chat/Composer';
import { MentionText } from '../chat/MessageRow';
import { ReactionBar, ReactionPicker } from '../chat/Reactions';
import { formatFullDate, toDate } from '../chat/chatUtils';
import { getComments, createComment, deleteComment, editComment, reactToComment, pinComment, getCommentMentionable } from '../../../services/api';
import { toast } from '../../../hooks/use-toast';

const COMMENT_MAX = 2000;

const relative = (ts) => {
  const d = toDate(ts); if (!d) return '';
  const s = (Date.now() - d) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const CommentRow = ({ c, me, depth, canModerate, members, state, actions }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const u = c.user || {};
  const isOwn = c.userId === me.id;
  const mentionsMe = (c.mentions || []).some((m) => m.id === me.id);
  const isEditing = state.editingId === c.id;
  const isReplying = state.replyToId === c.id;

  return (
    <div className={`group relative rounded-lg px-3 py-2.5 transition-colors hover:bg-zinc-800/30 ${mentionsMe ? 'bg-amber-950/20 border-l-2 border-amber-500' : ''} ${c.pinned && depth === 0 ? 'bg-amber-500/5 border border-amber-700/30' : ''}`} data-testid={`comment-${c.id}`}>
      <div className="flex gap-3 items-start">
        <Avatar className="w-8 h-8 rounded-full border border-zinc-700/80 flex-shrink-0 mt-0.5">
          <AvatarImage src={u.avatarUrl} />
          <AvatarFallback className="bg-zinc-800 text-amber-500 text-xs">{(u.displayName || '?').charAt(0).toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-headline text-sm tracking-wide ${u.role === 'owner' ? 'text-red-400' : isOwn ? 'text-amber-400' : 'text-zinc-200'}`}>{u.displayName || 'Anonymous'}</span>
            {u.role === 'owner' && <span className="bg-red-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded tracking-widest uppercase">Owner</span>}
            {u.role === 'moderator' && <Shield className="text-amber-400 w-3.5 h-3.5" title="Moderator" />}
            <RankChip rank={u.rank} />
            <span className="font-typewriter text-[10px] text-zinc-500" title={formatFullDate(c.createdAt)} data-testid="comment-time">{relative(c.createdAt)}</span>
            {c.pinned && <span className="flex items-center gap-0.5 text-[10px] font-typewriter text-amber-500" data-testid="pinned-label"><Pin className="w-3 h-3" />pinned</span>}
          </div>

          {isEditing ? (
            <div className="mt-1.5" data-testid={`edit-composer-${c.id}`}>
              <Composer initialValue={c.content} initialMentions={c.mentions || []} members={members} compact autoFocus maxLength={COMMENT_MAX}
                submitLabel="Save" placeholder="Edit your note…" testId="comment-edit-input"
                onSubmit={(text, ids) => actions.saveEdit(c.id, text, ids)} onCancel={() => actions.setEditingId(null)} />
            </div>
          ) : (
            <p className="text-sm text-zinc-200 leading-relaxed break-words [overflow-wrap:anywhere] whitespace-pre-wrap mt-0.5">
              <MentionText content={c.content} mentions={c.mentions} meId={me.id} />
              {c.editedAt && <span className="font-typewriter text-[10px] text-zinc-500 italic ml-1.5" title={`Edited ${formatFullDate(c.editedAt)}`} data-testid="edited-label">(edited)</span>}
            </p>
          )}

          {!isEditing && <ReactionBar reactions={c.reactions} meId={me.id} messageId={c.id} onToggle={(emoji) => actions.react(c.id, emoji)} />}

          {!isEditing && !isReplying && (
            <div className="flex items-center gap-1 mt-1.5 -ml-1 text-zinc-400">
              <ReactionPicker align="left" onPick={(emoji) => actions.react(c.id, emoji)} testId={`react-btn-${c.id}`} />
              <button onClick={() => actions.startReply(c.id)} className="flex items-center gap-1 px-1.5 py-1 rounded hover:bg-zinc-700 hover:text-amber-400 text-[11px] font-typewriter" data-testid={`reply-btn-${c.id}`}><Reply className="w-3.5 h-3.5" />Reply</button>
              {isOwn && <button onClick={() => actions.startEdit(c.id)} className="p-1 rounded hover:bg-zinc-700 hover:text-amber-400" title="Edit" data-testid={`edit-btn-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
              {canModerate && depth === 0 && <button onClick={() => actions.pin(c.id)} className={`p-1 rounded hover:bg-zinc-700 ${c.pinned ? 'text-amber-400' : 'hover:text-amber-400'}`} title={c.pinned ? 'Unpin' : 'Pin to top'} data-testid={`pin-btn-${c.id}`}><Pin className="w-3.5 h-3.5" /></button>}
              {(canModerate || isOwn) && (confirmDelete ? (
                <span className="flex items-center gap-0.5 ml-1">
                  <span className="text-[10px] font-typewriter text-red-400 px-1">Delete?</span>
                  <button onClick={() => { actions.remove(c.id); setConfirmDelete(false); }} className="p-1 rounded hover:bg-red-600/30 text-red-400" title="Confirm delete" data-testid={`confirm-delete-${c.id}`}><Check className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setConfirmDelete(false)} className="p-1 rounded hover:bg-zinc-700" title="Keep" data-testid={`cancel-delete-${c.id}`}><X className="w-3.5 h-3.5" /></button>
                </span>
              ) : (
                <button onClick={() => setConfirmDelete(true)} className="p-1 rounded hover:bg-zinc-700 hover:text-red-400" title="Delete" data-testid={`delete-btn-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              ))}
            </div>
          )}

          {isReplying && (
            <div className="bg-[#18181b] border-l-2 border-amber-500 p-2.5 mt-2 rounded-r-md shadow-inner" data-testid={`inline-reply-${c.id}`}>
              <Composer members={members} compact autoFocus maxLength={COMMENT_MAX} submitLabel="Reply" placeholder={`Reply to ${u.displayName || 'this note'}…`} testId="comment-reply-input"
                replyPreview={{ senderName: u.displayName || 'Unknown', content: c.content }}
                onSubmit={(text, ids) => actions.send(text, ids, c)} onCancel={() => actions.setReplyToId(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Replies to replies are flattened under the top-level comment so nothing squeezes off-screen on mobile
export const CommentThread = ({ postId, me, canModerate, onCountChange }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [replyToId, setReplyToId] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try { const data = await getComments(postId); setComments(data); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, [postId]);

  useEffect(() => { load(); getCommentMentionable(postId).then(setMembers).catch(() => {}); }, [load, postId]);
  useEffect(() => { onCountChange?.(comments.filter((c) => !c.isDeleted).length); }, [comments, onCountChange]);

  const fail = (e, fallback) => toast({ title: 'Comments', description: e?.response?.data?.detail || fallback, variant: 'destructive' });
  const patch = (id, data) => setComments((prev) => prev.map((c) => (c.id === id ? { ...c, ...data } : c)));

  const send = async (text, mentionIds, parent = null) => {
    try {
      const created = await createComment({ postId, content: text, parentCommentId: parent ? parent.id : null, mentions: mentionIds });
      created.user = { id: me.id, displayName: me.displayName || me.email?.split('@')[0], avatarUrl: me.avatarUrl, role: me.role, rank: me.rank };
      setComments((prev) => [...prev, created]);
      setReplyToId(null);
      if (!members.some((m) => m.id === me.id)) getCommentMentionable(postId).then(setMembers).catch(() => {});
    } catch (e) { fail(e, 'Failed to add comment'); throw e; }
  };
  const saveEdit = async (id, text, mentionIds) => {
    try { const res = await editComment(id, text, mentionIds); patch(id, res); setEditingId(null); }
    catch (e) { fail(e, 'Failed to edit comment'); throw e; }
  };
  const remove = async (id) => {
    try { await deleteComment(id); setComments((prev) => prev.filter((c) => c.id !== id && c.parentCommentId !== id)); }
    catch (e) { fail(e, 'Failed to delete comment'); }
  };
  const react = async (id, emoji) => {
    try { const res = await reactToComment(id, emoji); patch(id, res); }
    catch (e) { fail(e, 'Failed to react'); }
  };
  const pin = async (id) => {
    try { const res = await pinComment(id); patch(id, res); }
    catch (e) { fail(e, 'Failed to pin comment'); }
  };

  const actions = {
    send, saveEdit, remove, react, pin, setEditingId, setReplyToId,
    startReply: (id) => { setEditingId(null); setReplyToId(id); },
    startEdit: (id) => { setReplyToId(null); setEditingId(id); },
  };
  const state = { replyToId, editingId };

  const live = comments.filter((c) => !c.isDeleted);
  const byId = new Map(live.map((c) => [c.id, c]));
  const rootOf = (c) => { let cur = c; let hops = 0; while (cur.parentCommentId && byId.has(cur.parentCommentId) && hops < 20) { cur = byId.get(cur.parentCommentId); hops += 1; } return cur.id; };
  const tops = live.filter((c) => !c.parentCommentId || !byId.has(c.parentCommentId))
    .sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0) || toDate(a.createdAt) - toDate(b.createdAt));
  const repliesFor = (id) => live.filter((c) => c.parentCommentId && byId.has(c.parentCommentId) && rootOf(c) === id && c.id !== id).sort((a, b) => toDate(a.createdAt) - toDate(b.createdAt));

  return (
    <div className="bg-[#121215] border border-zinc-800 rounded-xl overflow-hidden" data-testid="comment-thread">
      <div className="h-11 px-4 border-b border-zinc-800/80 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-amber-500" />
        <h2 className="font-headline text-zinc-100 text-sm tracking-wider">INVESTIGATOR NOTES</h2>
        <span className="ml-auto text-[11px] font-typewriter text-zinc-500" data-testid="comment-count">{live.length} {live.length === 1 ? 'note' : 'notes'}</span>
      </div>

      <div className="p-2 sm:p-3 space-y-1">
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-amber-500 animate-spin" /></div>
        ) : tops.length === 0 ? (
          <p className="text-center text-zinc-500 py-8 font-typewriter text-sm">No investigator notes yet. Be the first to comment!</p>
        ) : tops.map((c) => (
          <div key={c.id}>
            <CommentRow c={c} me={me} depth={0} canModerate={canModerate} members={members} state={state} actions={actions} />
            {repliesFor(c.id).map((r) => (
              <div key={r.id} className="ml-5 sm:ml-10 border-l border-zinc-800 pl-1 sm:pl-2">
                <CommentRow c={r} me={me} depth={1} canModerate={canModerate} members={members} state={state} actions={actions} />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="border-t border-zinc-800/80 p-3 bg-[#121215]" data-testid="comment-composer">
        <Composer members={members} maxLength={COMMENT_MAX} placeholder="Add your investigator notes…" testId="comment-input" submitLabel="Post"
          onSubmit={(text, ids) => send(text, ids, null)} />
      </div>
    </div>
  );
};

export default CommentThread;
